<template>
  <k-modal id="add-comments-otj-modal" :show="showModal" @close="$emit('close')">
    <template #header>
      <h3>{{ action ? 'Approve OTJ' : 'Refer OTJ' }}</h3>
    </template>
    <template #body>
      <div v-if="otjData" class="otj-details-section">
        <h4>OTJ Details</h4>
        <div class="otj-detail-container">
          <div class="otj-detail-grid">
            <div class="otj-detail-item">
              <strong>
                Submitted:
              </strong>
              <span>{{ parseTimestamp(otjData.start_time, undefined, 'd MMM yyyy') }}</span>
              <k-tooltip
                text="Date the OTJ entry was submitted by the learner"
                trigger-label="More information about submission date"
              >
                <i class="fas fa-info-circle" aria-hidden="true"></i>
              </k-tooltip>
            </div>
            <div class="otj-detail-item">
              <strong>
                Activity Start:
              </strong>
              <span>{{ parseTimestamp(otjData.start_time, undefined, 'd MMM yyyy HH:mm') }}</span>
              <k-tooltip
                text="Date the activity took place"
                trigger-label="More information about activity date"
              >
                <i class="fas fa-info-circle" aria-hidden="true"></i>
              </k-tooltip>
            </div>
            <div class="otj-detail-item">
              <strong>Time Spent:</strong>
              <span>{{ formatTimeSpent(otjData.time_spent) }}</span>
            </div>
            <div class="otj-detail-item">
              <strong>Learning Activity Type:</strong>
              <span>{{ otjData.category }}</span>
            </div>
          </div>

          <div class="otj-detail-full-width">
            <div class="otj-detail-item-full">
              <strong>Learning Description:</strong>
              <span>{{ otjData.learning_description }}</span>
            </div>
            <div v-if="otjData.competencies && otjData.competencies.length > 0" class="otj-detail-item-full">
              <strong>Competencies:</strong>
              <div class="competencies-tags">
                <span v-for="competency in otjData.competencies" :key="competency.id" class="badge competency-tag">
                  {{ competency.label }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form>
        <label for="otj-comment">Please provide a comment for this OTJ</label>
        <fieldset class="form-group form-entry">
          <textarea id="otj-comment" placeholder="Add a comment" class="form-control" v-model="coachComment"></textarea>
        </fieldset>
      </form>
    </template>
    <template #footer>
      <button class="btn btn-outlined" @click="$emit('close')">Close</button>
      <button class="btn" :class="action ? 'btn-success' : 'btn-warning'" @click="submitComments" :disabled="disableButton">
        {{ action ? 'Approve' : 'Refer' }}
      </button>
    </template>
  </k-modal>
</template>

<style scoped>
.fa-info-circle {
  position: relative;
  top: -5px;
  left: -2px;
  font-size: 0.9rem;
}

.otj-details-section h4 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 1.2rem;
}

.otj-detail-container {
  background-color: var(--kate-panel-light);
  border: var(--kate-panel-light-border);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.otj-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.otj-detail-item {
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
}

.otj-detail-full-width {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.otj-detail-item-full {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.otj-detail-item strong,
.otj-detail-item-full strong {
  color: var(--kate-type-light);
  letter-spacing: 0.5px;
}

.otj-detail-item span,
.otj-detail-item-full span {
  color: var(--kate-type-light);
  font-size: 1rem;
  line-height: 1.4;
}

.competencies-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KModal from '@base-components/k-modal.vue';
import { KTooltip } from '@kate/components';

export default {
  components: {
    KModal,
    KTooltip,
  },
  mixins: [TimeMixin],
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    otjData: {
      type: Object,
      required: true,
    },
    programmeId: {
      type: [String, Number],
      required: true,
    },
    programmeMemberId: {
      type: [String, Number],
      required: true,
    },
    action: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      coachComment: '',
    };
  },
  computed: {
    disableButton() {
      if (this.action === true) {
        return false;
      }
      return !this.coachComment || !this.otjData;
    },
  },
  methods: {
    submitComments() {
      if (!this.coachComment && this.action === true) {
        this.$emit('update-status', this.otjData.id, this.action);
        this.closeModal();
      } else {
        this.addFeedbackToOtjEntry(this.otjData?.id);
      }
    },
    addFeedbackToOtjEntry(otjId) {
      const jsonPayload = {
        comment: this.coachComment,
        programme_member_id: this.programmeMemberId,
      };
      this.$logger.info('Adding feedback to OTJ entry', { otjId, action: this.action }, true);
      this.$http.post(`/api/curriculum/programmes/${this.programmeId}/otj-entries/${otjId}/feedback`, jsonPayload)
        .then(() => {
          this.$logger.info('Feedback added to OTJ entry', { otjId });
          this.$emit('update-status', otjId, this.action);
          this.closeModal();
        })
        .catch(err => {
          this.$logger.error('Error adding feedback to OTJ entry', { otjId }, err);
          this.$ktoast.showError(err, true);
        });
    },
    closeModal() {
      this.$emit('close');
      this.coachComment = '';
    },
  },
};
</script>
